/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Input,
  Button,
  useToast,
} from "@coreai/component-library";
import { useTheme } from "@emotion/react";
import SuggestionTextField from "./SuggestionTextField";
import {
  createProject,
  ProjectsModel,
} from "../../../../services/projectsService";
import { useLanguage } from "LanguageContext";
import AdvertiserSearch from "../../../MainComponents/Projects/AdvertiserSearch";
interface AddProjectDialogProps {
  open: boolean;
  onClose: () => void;
  suggestions: ProjectsModel[];
  saveProject: (projectName: string) => void;
  defaultAdvertiser?: string | undefined;
  newProject?: (newProject: any) => void;
  projectInputValue?: string;
}

const AddProjectDialog: React.FC<AddProjectDialogProps> = ({
  open,
  onClose,
  suggestions,
  saveProject,
  defaultAdvertiser,
  newProject,
  projectInputValue,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const [advertiser, setAdvertiser] = useState<string>(defaultAdvertiser || "");
  const [projectName, setProjectName] = useState<string>("");
  const { toast } = useToast();
  const projectInputRef = React.useRef<HTMLInputElement>(null); // Explicitly type the ref

  const alertDialogBodyStyles = {
    border: "none",
  } as React.CSSProperties;

  const compulsoryStyles = {
    fontFamily: theme.typography.subbody_medium.fontFamily,
    fontSize: theme.typography.subbody_medium.fontSize,
    fontWeight: theme.typography.subbody_medium.fontWeight,
    color: theme.typography.subbody_medium.colors,
    paddingTop: theme.spacing.xl,
  } as React.CSSProperties;

  const bodyStyles = {
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.sm,
    paddingTop: 0,
    overflowY: "visible",
  } as React.CSSProperties;

  useEffect(() => {
    setProjectName(projectInputValue || "");
  },[projectInputValue]);

  const onSave = useCallback(() => {
    createProject({
      advertiser,
      name: projectName,
    }).then((result) => {
      toast({
        variant: 'success',
        title: t("project_toast_add_success"),
     });
     newProject?.(result);
     saveProject(projectName);
    }).catch((error) => {
      if (error.status === 400) {
        toast({
          variant: 'error',
          title: t("error_title"),
          description: t("save_project_toast_error_message", {
            projectName,
            advertiserName: advertiser,
          }),
        });
      }
      console.log({error})        
    });
  }, [advertiser, projectName]);

  const setFocuseAdver = () => {
    if (projectInputRef.current) {
      projectInputRef.current.focus();    
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent size="sm">
        <AlertDialogHeader style={alertDialogBodyStyles}>
          <AlertDialogTitle>{t("alertDialog_add_project")}</AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogBody style={bodyStyles}>
          <div>{t("alertDialog_add_project_description")}</div>

          <div style={{ marginBottom: "16px", marginTop: "16px" }}>
            <AdvertiserSearch
                setAdvertiser={(advertiser) => setAdvertiser(advertiser)}
                focuseNext={setFocuseAdver}
                advertiser={defaultAdvertiser}
              />
          </div>

          <div>
            <Input
              label={t("alertDialog_add_project_label_project")}
              style={{ width: "100%" }}
              placeholder={t("project_newProject_placeHolder_project")}
              onChange={(ev) => setProjectName(ev.target.value)}
              autoComplete="off"
              ref={projectInputRef}
              required
              value={projectName}
            />
          </div>
          <div style={compulsoryStyles}>{t("core_compulsory_field")}</div>
        </AlertDialogBody>

        <AlertDialogFooter style={alertDialogBodyStyles}>
          <AlertDialogCancel asChild>
            <Button variant="tertiary">{t("core_cancel")}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant={"brand"}
              style={{ backgroundColor: theme.colors.primary }}
              onClick={onSave}
              disabled={advertiser === "" || projectName === ""}
            >
              {t("core_save")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AddProjectDialog;
